<template>
    <div class="border rounded p-3 mb-4">
        <h6>Korr och tryckfil</h6>
        <production-status :state="state" :manual-image="manualImage" :error="error" :notice="notice" />
        <div v-if="!state.unsaved_source_selection" class="d-flex flex-wrap align-items-center" style="gap: 8px;">
            <!-- Steg 1: En design behöver sparas som korr. -->
            <template v-if="needsProof">
                <router-link
                    v-if="canDesign" class="btn btn-primary"
                    :to="{ name: 'cardorders.greeting-design', params: { id: orderId } }"
                >Designa hälsning</router-link>
                <button
                    v-if="production.step === 'needs_proof'"
                    type="button"
                    class="btn"
                    :class="[ state.proof_source === 'manual_image' ? 'btn-primary' : 'btn-light' ]"
                    :disabled="busy"
                    @click="$emit('create-proof')"
                >{{ state.proof_source === 'manual_image' ? 'Skapa korr' : 'Skapa korr från sparad design' }}</button>
                <button
                    v-if="approved && !validPrint"
                    type="button"
                    class="btn btn-light"
                    :disabled="busy"
                    @click="$emit('create-print')"
                >Skapa tryckfil</button>
            </template>

            <!-- Steg 2–4: Det finns ett korr med sparat tryckunderlag. -->
            <template v-else>
                <!-- v-show behåller dialogen medan godkännandet uppdaterar orderns status. -->
                <approve-proof-button
                    v-show="needsApproval" :order-id="orderId" :proof="proof" :revision="state.revision" :disabled="busy"
                    button-label="Granska och godkänn korr" button-class="btn btn-primary" @approved="updated"
                />

                <!-- Steg 2: Ett nytt korr väntar. Tidigare godkänt korr kan fortfarande användas. -->
                <template v-if="needsApproval">
                    <button v-if="approved && !validPrint" type="button" class="btn btn-light" :disabled="busy" @click="$emit('create-print')">Skapa tryckfil från godkänt korr</button>
                </template>

                <!-- Steg 3: Korret är godkänt men saknar aktuell tryckfil. -->
                <template v-else-if="production.step === 'needs_print'">
                    <button type="button" class="btn btn-primary" :disabled="busy || !approved" @click="$emit('create-print')">Skapa tryckfil</button>
                </template>

                <!-- Steg 4: Tryckfilen är klar för nedladdning. -->
                <button v-else-if="production.step === 'ready'" type="button" class="btn btn-primary" :disabled="busy" @click="download">Ladda ner tryckfil</button>
            </template>

            <span v-if="busy" class="text-muted" role="status">{{ progress }}</span>
        </div>

        <div class="d-flex flex-wrap mt-3" style="gap: 16px;">
            <!-- Korr som går att granska. Båda visas när det aktuella korret ersätter ett tidigare godkänt. -->
            <a v-if="proof" :href="proof.url" target="_blank" rel="noopener">Visa korr</a>
            <a v-if="approved && (!proof || approved.id !== proof.id)" :href="approved.url" target="_blank" rel="noopener">Visa godkänt korr</a>

            <!-- Aktuell tryckfil eller en tidigare version. -->
            <template v-if="validPrint">
                <button v-if="needsApproval" type="button" class="btn btn-link p-0" :disabled="busy" @click="download">Ladda ner tryckfil från godkänt korr</button>
                <a :href="printFile.url" target="_blank" rel="noopener">Visa tryckfil</a>
            </template>
            <a v-else-if="printFile" :href="printFile.url" target="_blank" rel="noopener">Visa tidigare tryckfil</a>

            <!-- När korr redan finns visas designåtgärderna som sekundära länkar. -->
            <template v-if="!needsProof && !state.unsaved_source_selection">
                <router-link v-if="canDesign" :to="{ name: 'cardorders.greeting-design', params: { id: orderId } }">Ändra design</router-link>
            </template>
            <button type="button" class="btn btn-link p-0" :disabled="busy" @click="$emit('history')">Tryckfil och historik</button>
        </div>
    </div>
</template>
<script>
import apiClient from '@services/ApiClient';
import { productionState, downloadPrint, productionError } from '@services/GreetingProductionApi';
import ProductionStatus from '@components/Greeting/ProductionStatus.vue';
import ApproveProofButton from '@components/Greeting/ApproveProofButton.vue';

export default {
    components: { ProductionStatus, ApproveProofButton },
    props: {
        orderId: { type: [Number, String], required: true },
        state: { type: Object, required: true },
        canDesign: { type: Boolean, default: true },
        manualImage: { type: Boolean, default: false },
    },
    data: () => ({
        busy: false, error: '', notice: '', progress: '',
    }),
    computed: {
        production() { return productionState(this.state); },
        proof() { return this.production.proof; },
        approved() { return this.production.approved; },
        printFile() { return this.production.printFile; },
        needsProof() { return this.production.needsProof; },
        needsApproval() { return this.production.needsApproval; },
        validPrint() { return this.production.validPrint; },
    },
    methods: {
        updated(state) { this.$emit('updated', state); },
        async download() {
            if (this.busy || !this.validPrint) return;
            this.busy = true; this.error = ''; this.notice = ''; this.progress = 'Laddar ner tryckfil…';
            try { await downloadPrint(apiClient, this.orderId, this.printFile); } catch (error) { this.error = await productionError(error); } finally { this.busy = false; }
        },
    },
};
</script>
